/**
 * ChatGPT 스타일 답변 입력창 보조 스크립트.
 *
 * 책임:
 *  - [data-attach-input] 파일 선택 시 상단 attachments 영역에 chip 렌더
 *  - chip의 × 클릭 시 DataTransfer로 input.files 재구성해 해당 파일만 제외
 *  - [data-autosize] textarea 자동 높이 조절 (max 240px CSS)
 *  - Enter(Shift 없이) 또는 Ctrl/Cmd+Enter 로 submit
 *  - 서버에서 redirect 응답 시 페이지 이동되므로 reset 처리는 불필요하지만,
 *    AJAX 응답이 폼 reset만 트리거하는 경우를 대비해 'reset' 이벤트도 정리.
 */

const IMAGE_MIME_PREFIX = "image/";

export function initChatComposer(form: HTMLFormElement): void {
    if (form.dataset.chatComposerInit === "1") {
        return;
    }
    form.dataset.chatComposerInit = "1";

    const textarea = form.querySelector<HTMLTextAreaElement>("[data-autosize]");
    const attachInput = form.querySelector<HTMLInputElement>("[data-attach-input]");
    const attachmentsBox = form.querySelector<HTMLElement>("[data-attachments]");

    if (textarea) {
        bindAutosize(textarea);
        bindEnterSubmit(textarea, form);
    }
    if (attachInput && attachmentsBox) {
        attachInput.addEventListener("change", () => renderAttachments(attachInput, attachmentsBox));
        bindDragAndDrop(form, attachInput, attachmentsBox);
    }
    form.addEventListener("reset", () => {
        if (textarea) {
            requestAnimationFrame(() => resizeTextarea(textarea));
        }
        if (attachmentsBox) {
            attachmentsBox.innerHTML = "";
            attachmentsBox.hidden = true;
        }
    });
}

function bindAutosize(textarea: HTMLTextAreaElement): void {
    const resize = () => resizeTextarea(textarea);
    textarea.addEventListener("input", resize);
    // 초기 1회
    requestAnimationFrame(resize);
}

function resizeTextarea(textarea: HTMLTextAreaElement): void {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
}

function bindEnterSubmit(textarea: HTMLTextAreaElement, form: HTMLFormElement): void {
    textarea.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        // Ctrl/Cmd+Enter 또는 그냥 Enter (Shift 없이) → submit, Shift+Enter는 줄바꿈
        if (e.shiftKey) return;
        e.preventDefault();
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
        } else {
            form.submit();
        }
    });
}

function renderAttachments(input: HTMLInputElement, box: HTMLElement): void {
    box.innerHTML = "";
    const files = input.files;
    if (!files || files.length === 0) {
        box.hidden = true;
        return;
    }
    box.hidden = false;

    Array.from(files).forEach((file, idx) => {
        const chip = document.createElement("div");
        chip.className = "admin-chat-composer__chip";

        if (file.type.startsWith(IMAGE_MIME_PREFIX)) {
            const img = document.createElement("img");
            img.className = "admin-chat-composer__chip-thumb";
            img.alt = "";
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result;
                if (typeof result === "string") img.src = result;
            };
            reader.readAsDataURL(file);
            chip.appendChild(img);
        } else {
            const icon = document.createElement("span");
            icon.className = "admin-chat-composer__chip-icon";
            icon.innerHTML = '<i class="fa-solid fa-file" aria-hidden="true"></i>';
            chip.appendChild(icon);
        }

        const name = document.createElement("span");
        name.className = "admin-chat-composer__chip-name";
        name.textContent = file.name;
        name.title = file.name;
        chip.appendChild(name);

        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "admin-chat-composer__chip-remove";
        remove.setAttribute("aria-label", `${file.name} 첨부 제거`);
        remove.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
        remove.addEventListener("click", () => removeFileAt(input, idx, box));
        chip.appendChild(remove);

        box.appendChild(chip);
    });
}

function removeFileAt(input: HTMLInputElement, indexToRemove: number, box: HTMLElement): void {
    const files = input.files;
    if (!files) return;
    const dt = new DataTransfer();
    Array.from(files).forEach((f, i) => {
        if (i !== indexToRemove) dt.items.add(f);
    });
    input.files = dt.files;
    renderAttachments(input, box);
}

/**
 * composer 박스를 drop zone 으로 만든다. 드래그한 파일들은 기존 첨부에 누적 추가.
 */
function bindDragAndDrop(zone: HTMLElement, input: HTMLInputElement, box: HTMLElement): void {
    let counter = 0;
    const cls = "admin-chat-composer--dragover";

    const hasFiles = (e: DragEvent): boolean => {
        const types = e.dataTransfer?.types;
        if (!types) return false;
        // DataTransferItemList 는 contains/includes 둘 다 안 되므로 forEach 로 검사
        for (let i = 0; i < types.length; i++) {
            if (types[i] === "Files") return true;
        }
        return false;
    };

    zone.addEventListener("dragenter", (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        counter++;
        zone.classList.add(cls);
    });

    zone.addEventListener("dragover", (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    });

    zone.addEventListener("dragleave", () => {
        counter = Math.max(0, counter - 1);
        if (counter === 0) zone.classList.remove(cls);
    });

    zone.addEventListener("drop", (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        counter = 0;
        zone.classList.remove(cls);

        const dropped = e.dataTransfer?.files;
        if (!dropped || dropped.length === 0) return;

        const merged = new DataTransfer();
        if (input.files) {
            Array.from(input.files).forEach((f) => merged.items.add(f));
        }
        Array.from(dropped).forEach((f) => merged.items.add(f));
        input.files = merged.files;
        renderAttachments(input, box);
    });
}
